import React, { useEffect, useState } from 'react';
import { Button, Grid, PageHeader, Row } from 'react-bootstrap';
import DatePicker from 'react-date-picker';
import BaseTable from 'react-base-table';
import { toast } from 'react-toastify';
import FuzzySearch from 'fuzzy-search';
import { useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import LDClient from 'launchdarkly-js-client-sdk';
import Select from 'react-select';
import yyyymmdd from '../libs/date_convert';
import {
  GetJitManagement,
  OpsLocations,
  UpdateJITLocationStatus,
} from '../libs/GraphQL';
import { COLUMNS, REASONS } from '../constants/JitManagement';
import ConfirmationModal from '../components/ConfirmationModal';
import 'react-toastify/dist/ReactToastify.css';
import './ViewDropOffSummary.css';
import JitStatusBulkUpdateModal from '../components/BulkUpdateModal/JitStatusBulkUpdateModal';
import { prettyToastOpts } from '../utils/toast';

const regionsList = [
  { value: 'MW', label: 'MW' },
  { value: 'NE', label: 'NE' },
  { value: 'WC', label: 'WC' },
  { value: 'S', label: 'S' },
];
const ViewJitManagement = () => {
  const [date, setDate] = useState({
    current: new Date(),
    search: yyyymmdd(new Date(), '-'),
  });
  const [data, setData] = useState({
    packingSlip: [],
    locationRoutes: [],
    result: [],
    initResult: [],
  });
  const [showData, setShowData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [updateJitDisabled, setUpdateJitDisabled] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [bulkUpdateDisabled, setBulkUpdateDisabled] = useState(true);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [checkedBulkItemsCount, setCheckedBulkItemsCount] = useState(0);
  const [modifiedStatusItemsCount, setModifiedStatusItemsCount] = useState(0);
  const [sortBy, setSortBy] = useState({});
  const [locationIdSearchbarValue, setLocationIdSearchbarValue] = useState('');
  const [routeIdSearchbarValue, setRouteIdSearchbarValue] = useState('');
  const [fridgeLocations, setFridgeLocations] = useState([]);
  const [regionFilter, setRegionFilter] = useState([]);
  const [fulfillmentCentersList, setFulfillmentCentersList] = useState([]);
  const [fulfillmentCentersFilter, setFulfillmentCentersFilter] = useState([]);

  const history = useHistory();

  useEffect(() => {
    const LDCUser = {
      key: Auth.user.attributes.email,
    };

    const ldclient = LDClient.initialize(
      process.env.REACT_APP_LAUNCH_DARKLY_CMS,
      LDCUser,
    );

    ldclient.on('ready', () => {
      const allFlagsValue = ldclient.allFlags();

      if (!allFlagsValue['jit-management']) {
        // redirect to home if user haven't access to see this page
        history.push('/home');
      }
    });
  }, []);

  const getUniqueValues = (array) => {
    return array
      .map((item) => item.fulfillmentCenter)
      .filter((value) => value !== null);
  };

  useEffect(() => {
    OpsLocations({ type: 'FRIDGE' }).then((response) => {
      const locations = response.data.opsLocations.locations;
      setFridgeLocations(locations);

      const centers = [...new Set(getUniqueValues(locations))];

      let formattedCenters = centers.map((center) => ({ value: center }));
      formattedCenters = formattedCenters.sort((a, b) =>
        a.value.localeCompare(b.value),
      );
      setFulfillmentCentersList(formattedCenters);

      setIsPageLoading(false);
    });
  }, []);

  const handleDateChange = (value) => {
    setDate({
      current: value,
      search: yyyymmdd(value, '-'),
    });
  };

  const handleSearch = async () => {
    setIsLoading(true);

    let {
      data: {
        // eslint-disable-next-line prefer-const
        locationRoutes: { routes },
        // eslint-disable-next-line prefer-const
        jitLocations,
      },
    } = await GetJitManagement(
      { stockingDate: date.search },
      { dropOffDate: date.search },
    );

    // hide non-fridge locations
    const fridgeLocationIds = fridgeLocations.map((location) => location.id);
    const fridgeLocationsList = routes.filter((route) =>
      fridgeLocationIds.includes(route.id),
    );

    const result = fridgeLocationsList.map((route) => {
      // generate unique id for each row
      const extendedUniqueId = `${route.stockingDate}#${route.id}#${route.stockingIndex}`;

      // add fulfillmentCenter to each location
      const fulfillmentCenter = fridgeLocations.find(
        (location) => location.id === route.id,
      ).fulfillmentCenter;

      // combine routes data with jitLocations data
      const matchingJit = jitLocations.find(
        (jitLocation) => jitLocation.location === route.id,
      );

      function determineJitValue(jitObject) {
        if (!jitObject) return '';
        if (jitObject.jit === true) return 'Y';
        if (jitObject.jit === false) return 'N';
        return '';
      }

      const jitValue = determineJitValue(matchingJit);

      const email = matchingJit
        ? matchingJit.email
          ? matchingJit.email
          : ''
        : '';

      const reason = matchingJit
        ? matchingJit.reason
          ? matchingJit.reason
          : ''
        : '';

      return {
        ...route,
        jit: jitValue,
        extendedUniqueId,
        jitStatus: '-',
        jitStatusToUpdate: '-',
        jitStatusReason: reason,
        updatedBy: email,
        checked: false,
        fulfillmentCenter,
      };
    });

    // Sort by routeId and then by stop
    result.sort((a, b) => {
      if (a.route < b.route) return -1;
      if (a.route > b.route) return 1;

      if (a.stop < b.stop) return -1;
      if (a.stop > b.stop) return 1;

      return 0;
    });

    setData({
      locationRoutes: routes,
      initResult: result,
      result,
    });

    setShowData(true);
    setIsLoading(false);
  };

  const handleSearchClear = () => {
    setShowData(false);
    setData((prevState) => ({
      ...prevState,
      result: [],
    }));
  };

  const calcCountOfChangedItems = (dataList) => {
    let atLeastOneChecked = false;
    let checkedCount = 0;

    if (dataList.length) {
      dataList.forEach((currentItem) => {
        if (currentItem.jitStatusToUpdate !== '-') {
          checkedCount += 1;
          atLeastOneChecked = true;
        }
      });
    }

    setUpdateJitDisabled(!atLeastOneChecked);
    setModifiedStatusItemsCount(checkedCount);
  };

  const confirmUpdate = async (isBulk = false, bulkStatus, bulkReason) => {
    setIsLoading(true);
    const itemsToUpdate = [];
    const checkedItems = isBulk
      ? checkedBulkItemsCount
      : modifiedStatusItemsCount;
    let modifiedResult;

    if (isBulk) {
      modifiedResult = data.result.map((item) => {
        if (item.checked) {
          itemsToUpdate.push(item);

          return {
            ...item,
            jit: bulkStatus === 'On' ? 'Y' : 'N',
            jitStatus: bulkStatus,
            jitStatusReason: bulkReason,
            jitStatusToUpdate: '-',
            updatedBy: localStorage.getItem('userEmail'),
            checked: false,
          };
        }

        return item;
      });
    } else {
      modifiedResult = data.result.map((item) => {
        if (item.jitStatusToUpdate !== '-') {
          itemsToUpdate.push(item);

          return {
            ...item,
            jit: item.jitStatusToUpdate === 'On' ? 'Y' : 'N',
            jitStatus: item.jitStatusToUpdate,
            jitStatusToUpdate: '-',
            updatedBy: localStorage.getItem('userEmail'),
            checked: false,
          };
        }

        return item;
      });
    }

    try {
      const clearItemsToUpdate = itemsToUpdate.map((item) => {
        return {
          stockingId: item.extendedUniqueId,
          jitIndicator: item.jitStatusToUpdate === 'On',
          reason: isBulk ? bulkReason : item.jitStatusReason,
          emailAddress: localStorage.getItem('userEmail'),
        };
      });

      await UpdateJITLocationStatus(clearItemsToUpdate);

      setData((prevState) => ({
        ...prevState,
        initResult: modifiedResult,
        result: modifiedResult,
      }));

      setShowConfirmModal(false);
      setUpdateJitDisabled(true);
      setShowBulkUpdateModal(false);
      setBulkUpdateDisabled(true);

      toast.success(
        `Successfully updated JIT status for ${checkedItems} element${
          checkedItems > 1 ? 's' : ''
        }`,
        prettyToastOpts({ autoClose: 4000 }),
      );
    } catch (e) {
      console.error(e);
    }

    setIsLoading(false);
  };

  const onChangeTableEvent = (e) => {
    if (e.target.classList.contains('statusDropdown')) {
      onChangeJitStatus(e);
    }

    if (e.target.classList.contains('statusReason')) {
      onChangeReason(e);
    }
  };

  const onClickTableEvent = (e) => {
    if (e.target.classList.contains('checkbox')) {
      onClickCheckbox(e);
    }
  };

  const onChangeJitStatus = (e) => {
    const newJitStatusToUpdate = e.target.value;

    const modifiedResult = data.result.map((item) => {
      if (
        item.id === e.target.getAttribute('locationrouteid') &&
        item.extendedUniqueId === e.target.getAttribute('uniqueid')
      ) {
        return {
          ...item,
          jitStatusReason:
            e.target.value === '-'
              ? ''
              : item.jitStatusReason || REASONS[0].value,
          jitStatusToUpdate: newJitStatusToUpdate,
        };
      }

      return item;
    });

    setData((prevState) => ({
      ...prevState,
      result: modifiedResult,
      initResult: modifiedResult,
    }));

    calcCountOfChangedItems(modifiedResult);
  };

  const onChangeReason = (e) => {
    const modifiedResult = data.result.map((item) => {
      if (
        item.id === e.target.getAttribute('locationrouteid') &&
        item.extendedUniqueId === e.target.getAttribute('uniqueid')
      ) {
        return {
          ...item,
          jitStatusReason: e.target.value,
        };
      }

      return item;
    });

    setData((prevState) => ({
      ...prevState,
      result: modifiedResult,
      initResult: modifiedResult,
    }));
  };

  const onClickCheckbox = (e) => {
    const currentId = e.target.getAttribute('locationrouteid');
    const currentUniqueId = e.target.getAttribute('uniqueid');
    const currentCheckboxState = e.target.checked;
    let checkedCount = 0;

    const modifiedResult = data.result.map((item) => {
      if (item.id === currentId && item.extendedUniqueId === currentUniqueId) {
        // eslint-disable-next-line no-param-reassign
        item.checked = currentCheckboxState;
      }

      if (item.checked) {
        checkedCount += 1;
      }

      return item;
    });

    setData((prevState) => ({
      ...prevState,
      result: modifiedResult,
    }));
    setBulkUpdateDisabled(!checkedCount > 0);
    setCheckedBulkItemsCount(checkedCount);
  };

  const openBulkUpdateModal = () => {
    setShowBulkUpdateModal(true);
  };

  const sortArrayOfObjects = (arr, key, order) => {
    return arr.sort((a, b) => {
      if (order === 'asc') {
        return a[key] > b[key] ? 1 : -1;
      }
      return a[key] < b[key] ? 1 : -1;
    });
  };

  const onColumnSort = ({ key, order }) => {
    const sortedData = sortArrayOfObjects(data.result, key, order);
    setSortBy({ key, order });
    setData((prevState) => ({
      ...prevState,
      result: sortedData,
    }));
  };

  const routeSearcher = new FuzzySearch(
    Object.values(data.initResult),
    ['route'],
    {
      caseSensitive: false,
    },
  );

  const locationSearcher = new FuzzySearch(
    Object.values(data.initResult),
    ['id'],
    {
      caseSensitive: false,
    },
  );

  const resetSearchesAndFilters = (exclude) => {
    if (locationIdSearchbarValue !== '' && exclude !== 'locationId')
      setLocationIdSearchbarValue('');
    if (routeIdSearchbarValue !== '' && exclude !== 'routeId')
      setRouteIdSearchbarValue('');
    if (fulfillmentCentersFilter.length && exclude !== 'fulfillmentCenter')
      setFulfillmentCentersFilter([]);
    if (regionFilter.length && exclude !== 'market') setRegionFilter([]);
  };

  const updateDataByFilter = (filterValue, filterKey) => {
    resetSearchesAndFilters(filterKey);

    if (!filterValue.length) {
      setData((prevState) => ({ ...prevState, result: prevState.initResult }));
    } else {
      const filterIds = filterValue.map((item) => item.value);
      const filteredData = data.initResult.filter((item) =>
        filterIds.includes(item[filterKey]),
      );

      setData((prevState) => ({ ...prevState, result: filteredData }));
    }
  };

  const handleFulfillmentCentersChange = (value) => {
    setFulfillmentCentersFilter(value);

    updateDataByFilter(value, 'fulfillmentCenter');
  };

  const handleRegionFilterChange = (value) => {
    setRegionFilter(value);

    updateDataByFilter(value, 'market');
  };

  const handleSearching = (e) => {
    if (e.target.name === 'routeIdSearchBox') {
      setRouteIdSearchbarValue(e.target.value);
      const searchedData = routeSearcher.search(e.target.value);
      setData((prevState) => ({
        ...prevState,
        result: searchedData,
      }));
      resetSearchesAndFilters('routeId');
    }

    if (e.target.name === 'locationIdSearchBox') {
      setLocationIdSearchbarValue(e.target.value);
      const searchedData = locationSearcher.search(e.target.value);
      setData((prevState) => ({
        ...prevState,
        result: searchedData,
      }));

      resetSearchesAndFilters('locationId');
    }
  };

  return (
    <>
      <PageHeader>
        JIT Management
        {showData && <small className="">{`(${date.search})`}</small>}
      </PageHeader>
      {!showData ? (
        <Grid>
          <Row className="text-center" style={{ marginBottom: '15px' }}>
            <div className="datePickerWrapper">
              <div className="datePickerLabel">Drop Off Date</div>
              <DatePicker
                data-test="Date-Picker"
                onChange={handleDateChange}
                value={date.current}
              />
            </div>
          </Row>
          <Row className="text-center" style={{ marginBottom: '15px' }}>
            <Button
              bsStyle="primary"
              bsSize="large"
              disabled={isLoading || isPageLoading}
              onClick={handleSearch}
            >
              {isLoading ? 'Loading...' : 'Search'}
            </Button>
          </Row>
        </Grid>
      ) : (
        <>
          <Button
            bsStyle="primary"
            bsSize="large"
            onClick={handleSearchClear}
            style={{ marginBottom: '15px', width: '100%' }}
          >
            Click to choose Another Date
          </Button>
          <h3>
            {`${data.result.length} Location Route${
              data.result.length === 1 ? '' : 's'
            }`}
          </h3>
          <div className="controlPanel">
            <span>
              <span className="searchBoxes">
                <span className="searchBoxesLabel">Filter: </span>
                <Select
                  options={regionsList}
                  placeholder="by Region"
                  getOptionLabel={(option) => option.value}
                  isMulti
                  hideSelectedOptions={false}
                  onChange={handleRegionFilterChange}
                  value={regionFilter.length ? regionFilter : null}
                  maxMenuHeight={400}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderRadius: '6px',
                      border: '1px solid gray',
                      width: '270px',
                    }),
                  }}
                />
                <Select
                  options={fulfillmentCentersList}
                  placeholder="by Market"
                  getOptionLabel={(option) => option.value}
                  isMulti
                  hideSelectedOptions={false}
                  onChange={handleFulfillmentCentersChange}
                  value={
                    fulfillmentCentersFilter.length
                      ? fulfillmentCentersFilter
                      : null
                  }
                  maxMenuHeight={400}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      borderRadius: '6px',
                      border: '1px solid gray',
                      width: '270px',
                    }),
                  }}
                />
              </span>
              <span className="searchBoxes">
                <span className="searchBoxesLabel">Search: </span>
                <input
                  type="text"
                  name="routeIdSearchBox"
                  className="searchBox"
                  onChange={handleSearching}
                  value={routeIdSearchbarValue}
                  placeholder="by Route ID"
                />
                <input
                  type="text"
                  name="locationIdSearchBox"
                  className="searchBox"
                  onChange={handleSearching}
                  value={locationIdSearchbarValue}
                  placeholder="by Location ID"
                />
              </span>
            </span>

            <span>
              <Button
                className="bulkUpdateBtn"
                bsSize="large"
                disabled={bulkUpdateDisabled}
                onClick={openBulkUpdateModal}
              >
                Bulk Update
              </Button>
              <Button
                className="btn-warning"
                bsSize="large"
                disabled={updateJitDisabled}
                onClick={() => setShowConfirmModal(true)}
              >
                Update JIT
              </Button>
            </span>
          </div>
          <BaseTable
            cellProps={{
              onClick: (e) => onClickTableEvent(e),
              onChange: (e) => onChangeTableEvent(e),
            }}
            data={data.result}
            columns={COLUMNS}
            rowKey="extendedUniqueId"
            width={1140}
            height={600}
            style={{ marginBottom: '40px' }}
            estimatedRowHeight={50}
            onColumnSort={onColumnSort}
            sortBy={sortBy}
            className="dropOffTable"
            rowClassName={({ rowData }) =>
              (rowData.jitStatus !== '-' ||
                rowData.stockedTimeUTC !== null ||
                rowData.jit === '' ||
                rowData.jit === 'N') &&
              'dropped'
            }
          />
        </>
      )}
      <JitStatusBulkUpdateModal
        show={showBulkUpdateModal}
        onHide={() => setShowBulkUpdateModal(false)}
        onConfirm={(status, reason) => confirmUpdate(true, status, reason)}
        checkedItems={checkedBulkItemsCount}
        loading={isLoading}
      />
      <ConfirmationModal
        title="Confirm status update"
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        onConfirm={() => confirmUpdate(false, '', '')}
        loading={isLoading}
      >
        Please confirm if you would like to update the status of{' '}
        {modifiedStatusItemsCount} order
        {modifiedStatusItemsCount > 1 ? 's' : ''}.
      </ConfirmationModal>
    </>
  );
};

export default ViewJitManagement;
